/**
 * Layout component that queries for data
 * with Gatsby's StaticQuery component
 *
 * See: https://www.gatsbyjs.org/docs/static-query/
 */
import { graphql, useStaticQuery } from "gatsby"
import React, { useCallback, useEffect, useState } from "react"
import styled, { ThemeProvider } from "styled-components"
import { Normalize } from "styled-normalize"
import { useSmoothScroll } from "../helpers/smooth-scroll"
import Footer from "../navigation/containers/footer/footer.container"
import theme from "../theme"
import { Modal, ModalType, PromoBar } from "../ui/"
import BackToTop from "./common/back-to-top.component"
import {
  commerceGridSpacing,
  gridSpacing,
} from "./common/general-styles.component"
import Banner, { BannerProps } from "./common/layout/banner.component"
import Header from "../navigation/containers/header/header.container"
import SkipLinksNavigation, {
  contentStartId,
} from "./common/skip-links-navigation.component"
import GlobalStyle from "./global-style.component"
import TranslateContentWrapper from "../helpers/translate-content-wrapper"
import { AppFunctionComponent, GatsbyImage, TextBuilder } from "../types"
import { SubmenuItem } from "../navigation/types/menu.interface"
import Cookies from "js-cookie"
import useTranslationHook from "../hooks/use-translation.hook"
import { CustomButtonProps } from "../ui/components/modal/popup/popup.component"

interface Props {
  masterPage?: boolean
  withSmoothScroll?: boolean
  withBackToTop?: boolean
  commerceCheckout?: boolean
  landing?: boolean
}

export const Main = styled.main<Props>`
  --grid-spacing: ${({ landing }) => (landing ? "0 !important" : "50px")};
  display: grid;
  grid-auto-rows: auto;
  grid-template-columns: 100%;
  ${({ masterPage }) => !masterPage && gridSpacing};
  ${({ commerceCheckout }) => commerceCheckout && commerceGridSpacing};
  grid-row-gap: var(--grid-spacing);

  @media (min-width: 960px) {
    padding-top: 0;
    margin-top: ${({ landing }) => (landing ? "0" : "15px")};
  }
`

interface LayoutProps extends Props {
  topNavigationItems: SubmenuItem[]
  footerNavigationItems: SubmenuItem[]
  promobarContent: TextBuilder
}

interface DataQuery {
  site: {
    siteMetadata: {
      title: string
    }
  }
  popup: {
    nodes: {
      buttonLink: string
      enabled: boolean
      buttonText: string
      backgroundImage: GatsbyImage
      node_locale: string
      style: {
        window: {
          web: {
            height: string
            width: string
          }
          mobile: {
            height: string
            width: string
          }
        }
        button: {
          web: CustomButtonProps
          mobile: CustomButtonProps
        }
        closeButton: {
          web: CustomButtonProps
          mobile: CustomButtonProps
        }
      }
    }[]
  }
  banner?: BannerProps
}

const Layout: AppFunctionComponent<LayoutProps> = ({
  children,
  masterPage,
  withBackToTop,
  withSmoothScroll,
  commerceCheckout,
  topNavigationItems,
  footerNavigationItems,
  promobarContent,
  landing,
}) => {
  const [active, setActive] = useState(false)
  const { site, banner, popup } = useStaticQuery<DataQuery>(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          title
        }
      }
      banner: contentfulSection(contentfulid: { eq: "breath-banner" }) {
        text {
          raw
        }
      }
      popup: allContentfulPopupWindow(
        filter: { entryName: { eq: "Default Popup" } }
      ) {
        nodes {
          buttonLink
          buttonText
          enabled
          backgroundImage {
            gatsbyImageData(quality: 100)
          }
          node_locale
          style {
            window {
              web {
                width
                height
              }
              mobile {
                width
                height
              }
            }
            button {
              mobile {
                width
                height
                top
                fontSize
              }
              web {
                backgroundColor
                height
                width
                bottom
                left
                borderRadius
                color
                fontFamily
                fontSize
                fontWeight
                textTransform
                lineHeight
              }
            }
            closeButton {
              mobile {
                width
                height
                top
              }
              web {
                backgroundColor
                height
                width
                top
                left
              }
            }
          }
        }
      }
    }
  `)
  const { language } = useTranslationHook()

  const activePopup = popup.nodes.find((el) =>
    el.node_locale.includes(language)
  )

  const desktopImage =
    activePopup?.backgroundImage.gatsbyImageData.images.fallback?.src
  const buttonLink = activePopup?.buttonLink
  const buttonText = activePopup?.buttonText
  const buttonMobileStyle = activePopup?.style.button.mobile
  const buttonWebStyle = activePopup?.style.button.web
  const closeButtonMobileStyle = activePopup?.style.closeButton.mobile
  const closeButtonWebStyle = activePopup?.style.closeButton.web
  const windowWebStyle = activePopup?.style.window.web
  const windowMobileStyle = activePopup?.style.window.mobile

  if (withSmoothScroll) {
    useSmoothScroll()
  }

  const setActiveModal = useCallback(() => {
    if (activePopup?.enabled) {
      const isModalShown = Cookies.get("popup")
      if (isModalShown) {
        setActive(false)
      } else {
        Cookies.set("popup", "true", { expires: 1 })
        setActive(true)
      }
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      setActiveModal()
    }, 4000)
    return () => clearInterval(timer)
  }, [setActiveModal])

  return (
    <ThemeProvider theme={theme}>
      <TranslateContentWrapper>
        <Normalize />
        <GlobalStyle />
        <SkipLinksNavigation />
        {banner && <Banner text={banner.text} />}
        {!landing && <PromoBar content={promobarContent} />}
        {activePopup && activePopup.enabled && (
          <Modal
            onClose={() => setActive(false)}
            active={active}
            modalType={ModalType.CustomPopup}
            popupProps={{
              desktopImage,
              buttonLink,
              buttonText,
              buttonMobileStyle,
              buttonWebStyle,
              closeButtonMobileStyle,
              closeButtonWebStyle,
              windowMobileStyle,
              windowWebStyle,
            }}
          />
        )}
        <Header
          navigationItems={topNavigationItems}
          siteTitle={site.siteMetadata.title}
        />
        <Main
          masterPage={masterPage}
          commerceCheckout={commerceCheckout}
          id={contentStartId}
          landing={landing}
        >
          {children}
        </Main>
        <Footer navigationItems={footerNavigationItems} />
        {withBackToTop && <BackToTop />}
      </TranslateContentWrapper>
    </ThemeProvider>
  )
}

export default Layout
